import { ReactComponent as CheckIcon } from "@assets/icons/checked_small.svg";
import { ReactComponent as PennyIcon } from "@assets/icons/penny_icon.svg";
import BottomBar from "@components/BottomBars/BottomBar";
import HeaderProgress from "@components/Progress/HeaderProgress";
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type FactFindProgressProps = {
  progress: {
    personal_details: boolean;
    financial_details: boolean;
    risk_reward: boolean;
  };
};

const FactFindProgress: React.FC<FactFindProgressProps> = ({ progress }) => {
  const navigate = useNavigate();
  const propertiesFactFindProgress = useMemo(() => {
    const arr = Object.values(progress).filter((i) => !!i).length;

    if (arr === 1) {
      return {
        active: 2,
        header: "One part down, two to go...",
        subtext:
          "Please continue filling the important  and get closer to your pension",
      };
    }

    if (arr === 2) {
      return {
        active: 3,
        header: "Almost there! Last part...",
        subtext:
          "Please continue filling the important information and get closer to your pension",
      };
    }

    if (arr === 3) {
      return {
        active: 4,
        header: "You're ready! Press get recommendation",
        subtext:
          "Once submitted, your pension recommendation will be on your dashboard within 24 hours.",
      };
    }

    return {
      active: 1,
      header: "Getting closer to the right pension",
      subtext: "What we'll go over today",
    };
  }, [progress]);

  const handleFactFind = (): string => {
    if (propertiesFactFindProgress.active === 4) {
      return "/";
    }

    return propertiesFactFindProgress.active === 1
      ? "personal-details"
      : propertiesFactFindProgress.active === 2
      ? "financial-details"
      : "risk-reward";
  };

  return (
    <section className="grow flex md:items-center md:justify-center border-t-4 border-l-0 md:border-t-0 md:border-l-4 border-[#F4E34E] px-0 md:px-2 lg:px-0">
      <div className="max-w-full md:max-w-[870px] w-full md:rounded-3xl md:border md:border-pn_gray-200">
        <HeaderProgress
          currentStep={
            propertiesFactFindProgress.active
              ? propertiesFactFindProgress.active - 1
              : 0
          }
          totalSteps={3}
          splited={3}
          closeButtonEvent={() => navigate("/")}
        />

        <div className="flex flex-col gap-y-2 md:gap-y-0 md:flex-row md:items-stretch">
          <div className="pl-4 pr-4 pt-6 pb-4 md:pl-8 md:pb-6 md:pr-6 flex-1">
            <div className="flex flex-col gap-y-6 p-0 md:p-4">
              <div className="bg-[#F4E34E] rounded-full w-[60px] h-[60px] flex items-center justify-center">
                <PennyIcon className="w-6 h-[27px]" />
              </div>
              <div className="flex flex-col gap-y-2">
                <span className="text-pn_gray-950 text-pn-large-title">
                  {propertiesFactFindProgress.header}
                </span>
                <span className="text-pn_gray-600 text-pn-headline-regular">
                  {propertiesFactFindProgress.subtext}
                </span>
              </div>
            </div>
          </div>

          <div className="py-4 md:py-6 pl-4 pr-4 md:pl-6 md:pr-8 flex flex-col gap-y-2 flex-1">
            <div className="px-4 py-6 rounded-2xl border border-pn_gray-200 bg-pn_white flex items-start gap-x-4">
              <div
                className={twMerge(
                  progress.personal_details
                    ? "bg-pn_green-500"
                    : propertiesFactFindProgress.active === 1
                    ? "bg-[#F4E34E]"
                    : "bg-pn_paper-3",
                  "rounded-full w-6 h-6 flex items-center justify-center text-pn-button-2"
                )}
              >
                {progress.personal_details ? <CheckIcon /> : 1}
              </div>

              <div className="flex-1 flex flex-col gap-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-pn-body-emphasis flex-1 text-pn_black">
                    Personal Details
                  </span>
                  <span className="text-pn-callout-regular">
                    {progress.personal_details ? "Completed" : "2 min"}
                  </span>
                </div>

                <span className="text-pn-callout-regular text-pn_gray-600">
                  To get the most out of your chat with your adviser, we'll ask
                  you for some basic information
                </span>
              </div>
            </div>

            <div className="px-4 py-6 rounded-2xl border border-pn_gray-200 bg-pn_white flex items-start gap-x-4">
              <div
                className={twMerge(
                  progress.financial_details
                    ? "bg-pn_green-500"
                    : propertiesFactFindProgress.active === 2
                    ? "bg-[#F4E34E]"
                    : "bg-pn_paper-3",
                  "rounded-full w-6 h-6 flex items-center justify-center text-pn-button-2"
                )}
              >
                {progress.financial_details ? <CheckIcon /> : 2}
              </div>

              <div className="flex-1 flex flex-col gap-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-pn-body-emphasis flex-1 text-pn_black">
                    Financial Details
                  </span>
                  <span className="text-pn-callout-regular">
                    {progress.financial_details ? "Completed" : "2 min"}
                  </span>
                </div>

                <span className="text-pn-callout-regular text-pn_gray-600">
                  Your answers will help us understand your financial needs.
                  (Remember, you don't need to be on a salary to start a
                  pension.)
                </span>
              </div>
            </div>

            <div className="px-4 py-6 rounded-2xl border border-pn_gray-200 bg-pn_white flex items-start gap-x-4">
              <div
                className={twMerge(
                  progress.risk_reward
                    ? "bg-pn_green-500"
                    : propertiesFactFindProgress.active === 3
                    ? "bg-[#F4E34E]"
                    : "bg-pn_paper-3",
                  "rounded-full w-6 h-6 flex items-center justify-center text-pn-button-2"
                )}
              >
                {progress.risk_reward ? <CheckIcon /> : 3}
              </div>

              <div className="flex-1 flex flex-col gap-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-pn-body-emphasis flex-1 text-pn_black">
                    Risk Profiler
                  </span>
                  <span className="text-pn-callout-regular">
                    {progress.risk_reward ? "Completed" : "4 min"}
                  </span>
                </div>

                <span className="text-pn-callout-regular text-pn_gray-600">
                  This is where we understand what you know about making
                  financial investments and what your preferences are for risk
                  taking.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex w-full pt-2 pb-6 px-8 items-center gap-x-2">
          <span className="text-pn-callout-regular text-pn_gray-600 flex-1">
            Your information is secure with Penny
          </span>
          <Link to={handleFactFind()} className="btn-primary-sm-40">
            {propertiesFactFindProgress.active === 1
              ? "Start"
              : propertiesFactFindProgress.active === 4
              ? "Get Recommendation"
              : "Continue"}
          </Link>
        </div>

        <div className="w-full block md:hidden mt-2">
          <BottomBar
            layout="message"
            message="Penny recommendation is ~8min away"
            labelNext={
              propertiesFactFindProgress.active === 1
                ? "Start"
                : propertiesFactFindProgress.active === 4
                ? "Get Recommendation"
                : "Continue"
            }
            onNext={handleFactFind()}
          />
        </div>
      </div>
    </section>
  );
};
export default FactFindProgress;
